import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";
import { DOMAINS, LEVELS, DISABILITIES } from '@/constants/ncfFramework';
// ─── Brand tokens ──────────────────────────────────────────────────────────
const T = {
  agniCore:  '#EA580C',
  agniGlow:  '#F97316',
  vidyaGold: '#C8A84B',
  nightInk:  '#0F1A2E',
  surface:   '#162035',
  border:    '#243350',
  textHi:    '#F1F5FF',
  textMid:   '#A8B8D0',
  textLow:   '#5C7090',
  ashWhite:  '#FDFCFA',
  slate:     '#111827',
  muted:     '#6B7280',
  blue:      '#2563EB',
  violet:    '#9333EA',
  green:     '#16A34A',
  teal:      '#0891B2',
  amber:     '#B45309',
  red:       '#DC2626',
  cardBg:    '#FFFFFF',
  pageBg:    '#F3F4F6',
};


// ─── Pure helpers ──────────────────────────────────────────────────────────
function levelFor(pct) {
  if (pct >= 85) return LEVELS[3];
  if (pct >= 55) return LEVELS[2];
  if (pct >= 20) return LEVELS[1];
  return LEVELS[0];
}

function domainScore(devRecord, domainId) {
  const domain = DOMAINS.find(d => d.id === domainId);
  if (!domain) return 0;
  const scores = domain.indicators.map(ind => {
    const rec = devRecord[`${domainId}::${ind.id}`];
    return LEVELS.find(l => l.id === rec?.level)?.score ?? 0;
  });
  const max = domain.indicators.length * 3;
  return max > 0 ? Math.round((scores.reduce((a, b) => a + b, 0) / max) * 100) : 0;
}

function calcAge(dob) {
  if (!dob) return null;
  const ms = Date.now() - new Date(dob).getTime();
  const y  = Math.floor(ms / (365.25 * 24 * 3600000));
  const m  = Math.floor((ms % (365.25 * 24 * 3600000)) / (30.44 * 24 * 3600000));
  return `${y}y ${m}m`;
}

// ─── Algorithmic suggestion engine ────────────────────────────────────────
// Pulls from DevelopmentRecord + FlashcardSession analytics to prioritise
// what the teacher should work on next with this child.
// Kosha → NCF domain mapping for suggestion bridging
const KOSHA_DOMAIN_MAP = {
  annamaya:    { label:"Physical Body",           color:"#0E8A7A", icon:"🏃" },
  pranamaya:   { label:"Vital Energy & Emotions", color:"#C2620A", icon:"💛" },
  manomaya:    { label:"Mind & Perception",       color:"#6E3EA6", icon:"🧠" },
  vijnanamaya: { label:"Intellect & Language",    color:"#1A4F7A", icon:"📚" },
  anandamaya:  { label:"Joy, Play & Creativity",  color:"#B03060", icon:"🎨" },
};

function buildSuggestions(devRecord, analytics, srtData) {
  const suggestions = [];

  // 1. Development record — find indicators stuck at emerging for >2 ratings
  DOMAINS.forEach(domain => {
    domain.indicators.forEach(ind => {
      const key = `${domain.id}::${ind.id}`;
      const rec = devRecord[key];
      if (!rec) return;
      if (rec.level === "emerging") {
        // Check history depth — if stuck at emerging
        const histCount = (rec.history || []).filter(h => h.level === "emerging").length;
        suggestions.push({
          type: "indicator-stuck",
          priority: histCount >= 2 ? "high" : "medium",
          domain: domain.id,
          domainLabel: domain.label,
          domainColor: domain.color,
          domainIcon:  domain.icon,
          label: ind.label,
          category: ind.category,
          reason: histCount >= 2
            ? `Rated "Emerging" ${histCount + 1} times — consider a different approach`
            : `Currently emerging — ready for focused practice`,
          action: `Focus on: ${ind.label}`,
        });
      }
      if (rec.level === "not-observed") {
        suggestions.push({
          type: "indicator-unrated",
          priority: "low",
          domain: domain.id,
          domainLabel: domain.label,
          domainColor: domain.color,
          domainIcon:  domain.icon,
          label: ind.label,
          category: ind.category,
          reason: "Not yet observed — introduce this skill",
          action: `Introduce: ${ind.label}`,
        });
      }
    });
  });

  // 2. BKT mastery — cards below 0.6 mastery that are due for review
  if (analytics?.masteryBySet) {
    analytics.masteryBySet.forEach(set => {
      const dueCards = (set.cards || []).filter(c =>
        c.masteryProb < 0.6 && new Date(c.nextReview) <= new Date()
      );
      if (dueCards.length > 0) {
        suggestions.push({
          type: "review-due",
          priority: dueCards.some(c => c.masteryProb < 0.35) ? "high" : "medium",
          domain: null,
          domainLabel: "Flashcard Mastery",
          domainColor: T.vidyaGold,
          domainIcon:  "🃏",
          label: `${set.setTitle} — ${dueCards.length} card${dueCards.length > 1 ? "s" : ""} due`,
          category: "SM-2 Review",
          reason: `Avg mastery: ${Math.round(set.avgMastery * 100)}% · ${dueCards.length} card${dueCards.length !== 1 ? "s" : ""} overdue for review`,
          action: "Run flashcard session",
          setTitle: set.setTitle,
        });
      }
    });
  }

  // 3. Domain gap — find weakest domain by devRecord score vs session performance
  const domainGaps = DOMAINS.map(d => {
    const devPct = domainScore(devRecord, d.id);
    return { ...d, devPct };
  }).sort((a, b) => a.devPct - b.devPct);

  const weakest = domainGaps[0];
  if (weakest && weakest.devPct < 40) {
    suggestions.push({
      type: "domain-weak",
      priority: weakest.devPct < 20 ? "high" : "medium",
      domain: weakest.id,
      domainLabel: weakest.label,
      domainColor: weakest.color,
      domainIcon:  weakest.icon,
      label: `${weakest.label} needs attention`,
      category: "Domain Gap",
      reason: `Domain score is ${weakest.devPct}% — below threshold for this age group`,
      action: `Create materials for ${weakest.label}`,
    });
  }

  // 4. SRT Panchakosha — low-scoring koshas from latest readiness session
  if (srtData?.koshaScores) {
    const koshaScores = srtData.koshaScores;
    Object.entries(koshaScores).forEach(([koshaId, pct]) => {
      if (typeof pct !== "number") return;
      if (pct < 45) {
        const km = KOSHA_DOMAIN_MAP[koshaId];
        if (!km) return;
        suggestions.push({
          type: "kosha-weak",
          priority: pct < 25 ? "high" : "medium",
          domain: null,
          domainLabel: "School Readiness",
          domainColor: km.color,
          domainIcon:  km.icon,
          label: `${km.label} kosha needs support`,
          category: "Panchakosha SRT",
          reason: `SRT score for ${km.label}: ${Math.round(pct)}% — below readiness threshold`,
          action: `Target activities for: ${km.label}`,
          koshaId,
        });
      }
    });
  }

  // Sort: high first, then medium, then low
  const order = { high: 0, medium: 1, low: 2 };
  return suggestions
    .sort((a, b) => order[a.priority] - order[b.priority])
    .slice(0, 8); // top 8
}

// ─── Radar chart (SVG) ─────────────────────────────────────────────────────
function RadarChart({ scores, size = 180 }) {
  const cx = size / 2, cy = size / 2, r = size * 0.36;
  const n  = scores.length;
  const angle = i => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pt = (i, ratio) => ({
    x: cx + Math.cos(angle(i)) * r * ratio,
    y: cy + Math.sin(angle(i)) * r * ratio,
  });
  const gridPoly = ratio => scores.map((_, i) => `${pt(i, ratio).x},${pt(i, ratio).y}`).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible" }}>
      {[0.25, 0.5, 0.75, 1].map(l => (
        <polygon key={l} points={gridPoly(l)}
          fill={l === 1 ? "none" : `rgba(37,99,235,0.04)`}
          stroke="rgba(37,99,235,0.12)" strokeWidth={0.5} />
      ))}
      {scores.map((_, i) => (
        <line key={i} x1={cx} y1={cy}
          x2={pt(i, 1).x} y2={pt(i, 1).y}
          stroke="rgba(37,99,235,0.1)" strokeWidth={0.5} />
      ))}
      <polygon
        points={scores.map((s, i) => `${pt(i, s.pct / 100).x},${pt(i, s.pct / 100).y}`).join(" ")}
        fill="rgba(37,99,235,0.12)" stroke={T.blue} strokeWidth={1.5} />
      {scores.map((s, i) => {
        const p = pt(i, s.pct / 100);
        const lp = pt(i, 1.22);
        return (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r={3} fill={s.color} />
            <text x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle"
              fontSize={9} fontWeight="700" fill={s.color} fontFamily="'Nunito',sans-serif">
              {s.icon}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Micro sparkline (last N ratings) ─────────────────────────────────────
function Sparkline({ history, color }) {
  if (!history || history.length < 2) return null;
  const pts = history.slice(-6).map((h, i, arr) => {
    const lvl = LEVELS.find(l => l.id === h.level);
    return { x: (i / (arr.length - 1)) * 48, y: 14 - (lvl?.score ?? 0) * (14 / 3) };
  });
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  return (
    <svg width={52} height={18} style={{ flexShrink: 0 }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={2} fill={color} opacity={i === pts.length - 1 ? 1 : 0.4} />
      ))}
    </svg>
  );
}

// ─── Mini progress bar ─────────────────────────────────────────────────────
function Bar({ pct, color, height = 6 }) {
  return (
    <div style={{ height, background: "#F3F4F6", borderRadius: height / 2, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: height / 2, transition: "width .5s ease" }} />
    </div>
  );
}

// ─── Add Child Modal ───────────────────────────────────────────────────────
function AddChildModal({ childForm, setChildForm, onAdd, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 300, padding: 16 }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 18, padding: 28, width: 460, maxWidth: "96vw",
        boxShadow: "0 24px 80px rgba(0,0,0,.25)" }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 20, fontWeight: 900, color: T.slate, marginBottom: 22 }}>
          Add Child Profile
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 5, letterSpacing: .8 }}>FULL NAME *</div>
          <input autoFocus value={childForm.name}
            onChange={e => setChildForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Child's full name"
            style={{ width: "100%", border: `1.5px solid #E5E7EB`, borderRadius: 9, padding: "10px 13px",
              fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
            onFocus={e => e.target.style.borderColor = T.agniCore}
            onBlur={e => e.target.style.borderColor = "#E5E7EB"} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 5, letterSpacing: .8 }}>DATE OF BIRTH</div>
            <input type="date" value={childForm.dob}
              onChange={e => setChildForm(f => ({ ...f, dob: e.target.value }))}
              style={{ width: "100%", border: `1.5px solid #E5E7EB`, borderRadius: 9, padding: "10px 11px", fontSize: 14, outline: "none" }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 5, letterSpacing: .8 }}>COLOUR TAG</div>
            <input type="color" value={childForm.color}
              onChange={e => setChildForm(f => ({ ...f, color: e.target.value }))}
              style={{ width: "100%", height: 42, border: `1.5px solid #E5E7EB`, borderRadius: 9, padding: 2, cursor: "pointer" }} />
          </div>
        </div>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 8, letterSpacing: .8 }}>DISABILITIES (select all that apply)</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {DISABILITIES.map(d => {
              const sel = childForm.disabilities.includes(d);
              return (
                <button key={d} onClick={() => setChildForm(f => ({
                  ...f, disabilities: sel ? f.disabilities.filter(x => x !== d) : [...f.disabilities, d],
                }))} style={{ padding: "4px 11px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: `1.5px solid ${sel ? T.agniCore : "#E5E7EB"}`,
                  background: sel ? "#FFF7ED" : "#fff", color: sel ? T.agniCore : T.muted }}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: 11, background: "#fff", border: `1.5px solid #E5E7EB`, borderRadius: 10,
              color: T.muted, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Cancel</button>
          <button onClick={onAdd} disabled={!childForm.name.trim()}
            style={{ flex: 2, padding: 11, background: childForm.name.trim() ? T.agniCore : "#D1D5DB",
              color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, cursor: childForm.name.trim() ? "pointer" : "not-allowed", fontSize: 14 }}>
            Add Child
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Session Log Modal (SM-2 flashcards + other modules) ──────────────────
function IndicatorRow({ ind, domainId, devRecord, onChange, domain }) {
  const key    = `${domainId}::${ind.id}`;
  const rec    = devRecord[key] || {};
  const level  = rec.level || "not-observed";
  const lvl    = LEVELS.find(l => l.id === level) || LEVELS[0];
  const hist   = rec.history || [];
  const [open, setOpen] = useState(false);

  return (
    <div style={{ borderBottom: `1px solid ${domain.border}40`, paddingBottom: 10, marginBottom: 10, lastChild: { borderBottom: "none", paddingBottom: 0, marginBottom: 0 } }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.slate, lineHeight: 1.4 }}>{ind.label}</div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 1 }}>{ind.category}</div>
        </div>
        {hist.length > 1 && <Sparkline history={[...hist].reverse()} color={domain.color} />}
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          {LEVELS.map(l => (
            <button key={l.id} onClick={() => onChange(key, { ...rec, level: l.id })}
              title={l.label}
              style={{ width: 34, height: 34, borderRadius: 8,
                border: `1.5px solid ${level === l.id ? l.color : "#E5E7EB"}`,
                background: level === l.id ? l.bg : "#fff",
                color: level === l.id ? l.color : "#D1D5DB",
                fontWeight: 800, fontSize: 11, cursor: "pointer", transition: "all .12s" }}>
              {l.short}
            </button>
          ))}
        </div>
        <button onClick={() => setOpen(o => !o)}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#D1D5DB", fontSize: 14, padding: "0 4px", flexShrink: 0 }}>
          {open ? "▲" : "▼"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, paddingLeft: 4 }}>
          <input value={rec.note || ""} onChange={e => onChange(key, { ...rec, note: e.target.value })}
            placeholder="Observation note (optional)…"
            style={{ width: "100%", border: `1px solid #E5E7EB`, borderRadius: 7, padding: "6px 10px",
              fontSize: 12, outline: "none", boxSizing: "border-box", color: T.slate, marginBottom: 10 }} />
          {hist.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginBottom: 6, letterSpacing: .8 }}>RATING HISTORY</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {[...hist].reverse().slice(0, 5).map((h, i) => {
                  const hl = LEVELS.find(l => l.id === h.level) || LEVELS[0];
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                      <span style={{ background: hl.bg, color: hl.color, border: `1px solid ${hl.color}40`,
                        borderRadius: 5, padding: "1px 7px", fontWeight: 700, minWidth: 72, textAlign: "center" }}>
                        {hl.label}
                      </span>
                      <span style={{ color: T.muted }}>{new Date(h.ratedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      {h.note && <span style={{ color: "#9CA3AF", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.note}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Module icon/label map — used by MasteryPanel session log display
const MODULE_TABS = [
  { id:"flashcard",    label:"Flashcards",    icon:"🃏" },
  { id:"flip",         label:"Flip Cards",    icon:"🃏" },
  { id:"quiz",         label:"Quiz",          icon:"🎯" },
  { id:"match",        label:"Match",         icon:"🧩" },
  { id:"worksheet",    label:"Worksheets",    icon:"📝" },
  { id:"activitycard", label:"Activity Cards", icon:"🖼️" },
  { id:"storybook",    label:"Story Books",   icon:"📖" },
];

// ─── Mastery panel (BKT/SM-2/Elo from FlashcardSession analytics) ─────────
function MasteryPanel({ childId, getAuthHeader }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setData(null);
    ;(async () => {
      try {
        const res = await fetch(`${API}/flashcard-sessions/${childId}/analytics?days=60`, { headers: getAuthHeader() });
        const d   = await res.json();
        if (d.success) setData(d.analytics);
      } catch {}
      setLoading(false);
    })();
  }, [childId]);

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
      <div style={{ width: 28, height: 28, border: `3px solid #E5E7EB`, borderTop: `3px solid ${T.agniCore}`, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
    </div>
  );

  if (!data) return (
    <div style={{ textAlign: "center", padding: 60, color: T.muted, fontSize: 14 }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🃏</div>
      No session data yet. Log a flashcard session to see SM-2 and BKT mastery here.
    </div>
  );

  const allModuleCounts = {
    flashcard:    data.flashcardSessions || 0,
    worksheet:    data.moduleCounts?.worksheet    || 0,
    activitycard: data.moduleCounts?.activitycard || 0,
    storybook:    data.moduleCounts?.storybook    || 0,
  };

  return (
    <div>
      {/* Summary row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 18 }}>
        {[
          { label: "Sessions (60d)",   value: data.totalSessions,          icon: "📅", color: T.blue    },
          { label: "Avg FC Accuracy",  value: `${data.overallAccuracy}%`,   icon: "🎯", color: T.green   },
          { label: "FC Completion",     value: `${data.overallCompletion}%`, icon: "✅", color: T.agniCore },
        ].map(m => (
          <div key={m.label} style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", textAlign: "center", border: `1.5px solid #E5E7EB` }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{m.icon}</div>
            <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 26, fontWeight: 900, color: m.color, lineHeight: 1 }}>{m.value}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* Module breakdown */}
      {Object.values(allModuleCounts).some(v => v > 0) && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, marginBottom: 16, border: `1.5px solid #E5E7EB` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 12, letterSpacing: .8 }}>MODULE ACTIVITY — 60 DAYS</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {MODULE_TABS.map(t => allModuleCounts[t.id] > 0 && (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 9, background: T.pageBg, borderRadius: 10, padding: "9px 14px", flex: 1, minWidth: 110 }}>
                <span style={{ fontSize: 22 }}>{t.icon}</span>
                <div>
                  <div style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 900, fontSize: 18, color: T.slate }}>{allModuleCounts[t.id]}</div>
                  <div style={{ fontSize: 11, color: T.muted }}>{t.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accuracy trend */}
      {data.accuracyTrend?.length > 1 && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, marginBottom: 16, border: `1.5px solid #E5E7EB` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 12, letterSpacing: .8 }}>🃏 FLASHCARD ACCURACY TREND</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 64 }}>
            {data.accuracyTrend.map((t, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <div title={`${t.accuracy}%`}
                  style={{ width: "100%",
                    background: t.accuracy >= 70 ? "#DCFCE7" : t.accuracy >= 40 ? "#FEF9C3" : "#FEE2E2",
                    borderTop: `3px solid ${t.accuracy >= 70 ? T.green : t.accuracy >= 40 ? T.amber : T.red}`,
                    height: `${Math.max(5, t.accuracy * 0.58)}px`,
                    borderRadius: "2px 2px 0 0", transition: "height .3s" }} />
                <div style={{ fontSize: 8, color: "#9CA3AF" }}>{new Date(t.date).getDate()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* BKT mastery per set */}
      {data.masteryBySet?.map(set => (
        <div key={String(set.setId)} style={{ background: "#fff", borderRadius: 12, padding: 16, marginBottom: 12, border: `1.5px solid #E5E7EB` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 14, color: T.slate }}>🃏 {set.setTitle}</div>
            <div style={{ fontSize: 12, color: T.muted }}>
              Avg mastery: <strong style={{ color: T.blue }}>{Math.round(set.avgMastery * 100)}%</strong>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {(set.cards || []).map(c => {
              const pct = Math.round(c.masteryProb * 100);
              const color = pct >= 85 ? T.green : pct >= 60 ? T.amber : pct >= 35 ? T.agniCore : "#9CA3AF";
              const bg    = pct >= 85 ? "#F0FDF4" : pct >= 60 ? "#FFFBEB" : pct >= 35 ? "#FFF7ED" : "#F9FAFB";
              const isDue = new Date(c.nextReview) <= new Date();
              return (
                <div key={c.cardId} title={`Next review: ${new Date(c.nextReview).toLocaleDateString("en-IN")}${isDue?" (DUE)":""}`}
                  style={{ background: bg, border: `1px solid ${color}40`, borderRadius: 8, padding: "4px 9px",
                    fontSize: 11, fontWeight: 700, color,
                    outline: isDue ? `2px solid ${color}` : "none" }}>
                  {c.cardLabel || c.cardId} · {pct}%
                  {isDue && <span style={{ marginLeft: 4, fontSize: 9, background: color, color: "#fff", padding: "0 4px", borderRadius: 3 }}>DUE</span>}
                  <span style={{ fontSize: 9, color: "#9CA3AF", fontWeight: 400, marginLeft: 4 }}>EF:{(c.ef||2.5).toFixed(1)} [{c.interval}d]</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Recent session log */}
      {data.recentLog?.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, border: `1.5px solid #E5E7EB` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 12, letterSpacing: .8 }}>RECENT SESSION LOG</div>
          {data.recentLog.map((s, i) => {
            const mt = MODULE_TABS.find(t => t.id === s.moduleType);
            return (
              <div key={s._id || i} style={{ display: "flex", alignItems: "flex-start", gap: 10,
                padding: "9px 0", borderBottom: i < data.recentLog.length - 1 ? `1px solid #F3F4F6` : "none" }}>
                <span style={{ fontSize: 16, marginTop: 1 }}>{mt?.icon || "📋"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: T.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {s.setTitle || "—"}
                    </span>
                    <span style={{ fontSize: 11, color: "#9CA3AF", flexShrink: 0 }}>
                      {new Date(s.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                  {s.notes && <div style={{ fontSize: 12, color: T.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📝 {s.notes}</div>}
                  {s.genNote && <div style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🌱 {s.genNote}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Suggestions panel ─────────────────────────────────────────────────────
function SuggestionsPanel({ suggestions, loading }) {
  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{ width: 24, height: 24, border: `3px solid #E5E7EB`, borderTop: `3px solid ${T.agniCore}`, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
    </div>
  );

  if (!suggestions.length) return (
    <div style={{ textAlign: "center", padding: 48, color: T.muted }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
      <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 16, fontWeight: 800, color: T.slate, marginBottom: 6 }}>All caught up!</div>
      <div style={{ fontSize: 13 }}>No urgent priorities right now. Keep rating indicators and logging sessions to generate smarter suggestions.</div>
    </div>
  );

  const PRIORITY_META = {
    high:   { label: "High Priority",   color: T.red,       bg: "#FEF2F2", dot: "#DC2626" },
    medium: { label: "Medium Priority", color: T.agniCore,  bg: "#FFF7ED", dot: T.agniCore },
    low:    { label: "Low Priority",    color: T.muted,     bg: T.pageBg,  dot: "#D1D5DB" },
  };

  const TYPE_ICON = {
    "indicator-stuck":  "⚠️",
    "indicator-unrated":"🔍",
    "review-due":       "🔄",
    "domain-weak":      "📉",
  };

  return (
    <div>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 18, lineHeight: 1.6 }}>
        Suggestions are generated from development indicator ratings, SM-2 review schedules, and BKT mastery probabilities.
        High-priority items reflect patterns across multiple data points.
      </div>
      {suggestions.map((s, i) => {
        const pm = PRIORITY_META[s.priority];
        return (
          <div key={i} style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", marginBottom: 10,
            border: `1.5px solid ${s.priority === "high" ? "#FECACA" : s.priority === "medium" ? "#FED7AA" : "#E5E7EB"}`,
            display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: pm.bg,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
              {TYPE_ICON[s.type] || "📌"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "'Nunito',sans-serif", fontSize: 14, fontWeight: 800, color: T.slate }}>
                  {s.action}
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                  background: pm.bg, color: pm.color, border: `1px solid ${pm.color}40` }}>
                  {pm.label}
                </span>
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>{s.reason}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 11, color: s.domainColor, background: `${s.domainColor}14`,
                  border: `1px solid ${s.domainColor}30`, borderRadius: 6, padding: "2px 8px", fontWeight: 700 }}>
                  {s.domainIcon} {s.domainLabel}
                </span>
                {s.category && (
                  <span style={{ fontSize: 11, color: T.muted }}>{s.category}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Print report ──────────────────────────────────────────────────────────
function printReport(selectedChild, devRecord, domainScores, overall) {
  const scores = DOMAINS.map(d => ({
    label: d.label, icon: d.icon, color: d.color, bg: d.bg,
    pct: domainScores.find(s => s.id === d.id)?.pct || 0,
    indicators: d.indicators.map(ind => ({
      label: ind.label,
      level: devRecord[`${d.id}::${ind.id}`]?.level || "not-observed",
      note:  devRecord[`${d.id}::${ind.id}`]?.note  || "",
    })),
  }));

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'Segoe UI',Arial,sans-serif;background:white;}
@media print{@page{size:A4 portrait;margin:0;}*{-webkit-print-color-adjust:exact!important;}}</style>
</head><body><div style="padding:24px 28px;">
  <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #EA580C;padding-bottom:10px;margin-bottom:18px;">
    <div style="display:flex;align-items:center;gap:10px;">
      <div style="background:#0F1A2E;color:white;font-weight:900;padding:4px 10px;border-radius:4px;font-size:13px;">NISH</div>
      <div style="font-size:11px;font-weight:700;color:#EA580C;">Vidyagni</div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:7px;color:#888;text-transform:uppercase;letter-spacing:.5px;">NCF-FS 2022 Progress Report</div>
      <div style="font-size:16px;font-weight:900;color:#111827;">${selectedChild.name}</div>
      <div style="font-size:8px;color:#6B7280;">${selectedChild.disabilities?.join(", ")||"—"} · Generated ${new Date().toLocaleDateString("en-IN")}</div>
    </div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:18px;">
    ${scores.map(d => `
      <div style="background:${d.bg};border-left:4px solid ${d.color};border-radius:0 8px 8px 0;padding:10px 12px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:5px;">
          <div style="font-size:11px;font-weight:800;color:${d.color};">${d.icon} ${d.label}</div>
          <div style="font-size:18px;font-weight:900;color:${d.color};">${d.pct}%</div>
        </div>
        <div style="height:4px;background:white;border-radius:3px;overflow:hidden;">
          <div style="width:${d.pct}%;height:100%;background:${d.color};"></div>
        </div>
      </div>`).join("")}
  </div>
  ${scores.map(d => `
    <div style="margin-bottom:14px;">
      <div style="font-size:10px;font-weight:800;color:${d.color};letter-spacing:.5px;margin-bottom:5px;">${d.icon} ${d.label.toUpperCase()}</div>
      <table style="width:100%;border-collapse:collapse;font-size:9.5px;">
        ${d.indicators.map(ind => {
          const lvl = LEVELS.find(l => l.id === ind.level) || LEVELS[0];
          return `<tr style="border-bottom:1px solid #F3F4F6;">
            <td style="padding:3px 6px;color:#374151;width:55%;">${ind.label}</td>
            <td style="padding:3px 6px;width:80px;">
              <span style="background:${lvl.bg};color:${lvl.color};font-weight:800;padding:1px 6px;border-radius:4px;font-size:8px;">${lvl.label}</span>
            </td>
            <td style="padding:3px 6px;color:#9CA3AF;font-style:italic;font-size:8px;">${ind.note}</td>
          </tr>`;
        }).join("")}
      </table>
    </div>`).join("")}
  <div style="margin-top:14px;border:1px solid #E5E7EB;border-radius:8px;padding:10px;">
    <div style="font-size:7.5px;color:#9CA3AF;font-style:italic;">
      Progress for children with diverse learning needs may not follow a linear trajectory.
      Indicators are aligned with NCF-FS 2022 benchmarks.
      This report should be reviewed alongside a qualified special educator or therapist. © NISH NCAHT ICMR · Vidyagni
    </div>
  </div>
</div></body></html>`;

  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 600);
}



// ─── Combined NCF + Panchakosha Report ────────────────────────────────────────
// Merges Progress Tracker (NCF indicators) + SRT (Panchakosha koshas) into one
// A4 printable report with clear labelling of what each measurement represents.
function printCombinedReport(selectedChild, devRecord, domainScores, overall, srtData) {
  const ptScores = DOMAINS.map(d => ({
    label: d.label, icon: d.icon, color: d.color, bg: d.bg,
    pct: domainScores.find(s => s.id === d.id)?.pct || 0,
    indicators: d.indicators.map(ind => ({
      label: ind.label,
      level: devRecord[`${d.id}::${ind.id}`]?.level || "not-observed",
      note:  devRecord[`${d.id}::${ind.id}`]?.note  || "",
    })),
  }));

  const srtOverall = srtData ? Math.round(srtData.overallScore ?? 0) : null;
  const srtDate    = srtData?.date
    ? new Date(srtData.date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })
    : "–";
  const srtItems   = srtData ? Object.values(srtData.responses || {}).filter(r => r.interacted).length : 0;

  const KOSHA_LIST = [
    { id:"annamaya",    label:"Annamaya Kosha",    subtitle:"Physical Body",           color:"#0E8A7A", icon:"🏃", ncfLabel:"Physical & Motor" },
    { id:"pranamaya",   label:"Pranamaya Kosha",   subtitle:"Vital Energy & Emotions", color:"#C2620A", icon:"💛", ncfLabel:"Socio-Emotional" },
    { id:"manomaya",    label:"Manomaya Kosha",     subtitle:"Mind & Perception",       color:"#6E3EA6", icon:"🧠", ncfLabel:"Cognitive" },
    { id:"vijnanamaya", label:"Vijnanamaya Kosha",  subtitle:"Intellect & Language",    color:"#1A4F7A", icon:"📚", ncfLabel:"Language & Numeracy" },
    { id:"anandamaya",  label:"Anandamaya Kosha",   subtitle:"Joy, Play & Creativity",  color:"#B03060", icon:"🎨", ncfLabel:"(No direct NCF equivalent)" },
  ];

  const lvColor = (pct) => pct >= 75 ? "#1A7A40" : pct >= 45 ? "#C07800" : pct > 0 ? "#C0392B" : "#8A99A8";
  const lvBg    = (pct) => pct >= 75 ? "#E8F5EE" : pct >= 45 ? "#FEF6E6" : pct > 0 ? "#FDECEA" : "#F0F4F8";
  const lvLabel = (pct) => pct >= 75 ? "Achieved" : pct >= 45 ? "Developing" : pct > 0 ? "Emerging" : "Not Observed";

  const ptLvColor = (pct) => pct >= 85 ? "#16A34A" : pct >= 55 ? "#B45309" : pct >= 20 ? "#EA580C" : "#9CA3AF";
  const ptLvBg    = (pct) => pct >= 85 ? "#F0FDF4" : pct >= 55 ? "#FFFBEB" : pct >= 20 ? "#FFF7ED" : "#F9FAFB";
  const ptLvLabel = (pct) => pct >= 85 ? "Achieved" : pct >= 55 ? "Developing" : pct >= 20 ? "Emerging" : "Not Observed";

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Segoe UI',Arial,sans-serif;background:white;font-size:10px;}
  @media print{@page{size:A4 portrait;margin:8mm 10mm;}*{-webkit-print-color-adjust:exact!important;}}
  .page-break{page-break-before:always;}
  table{border-collapse:collapse;width:100%;}
  td,th{vertical-align:top;}
</style>
</head><body>

<!-- ── PAGE 1: Summary ── -->
<div style="padding:0 0 20px;">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-end;
    border-bottom:3px solid #1A4F7A;padding-bottom:10px;margin-bottom:16px;">
    <div style="display:flex;align-items:center;gap:10px;">
      <div style="background:#0F1A2E;color:white;font-weight:900;padding:4px 10px;border-radius:4px;font-size:12px;">NISH</div>
      <div>
        <div style="font-size:8px;color:#888;font-weight:700;letter-spacing:.5px;">NATIONAL INSTITUTE FOR SPEECH AND HEARING</div>
        <div style="font-size:8px;color:#1A4F7A;font-weight:700;">Vidyagni · NCAHT · ICMR · FSLP</div>
      </div>
    </div>
    <div style="text-align:center;">
      <div style="font-size:14px;font-weight:900;color:#0F1A2E;">Combined Developmental Profile</div>
      <div style="font-size:7.5px;color:#888;margin-top:2px;">NCF-FS 2022 Progress Tracker + Panchakosha School Readiness Test</div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:9px;font-weight:700;color:#374151;">${selectedChild.name}</div>
      <div style="font-size:7.5px;color:#888;">${(selectedChild.disabilities||[]).join(", ")||"–"}</div>
      <div style="font-size:7.5px;color:#888;">Generated ${new Date().toLocaleDateString("en-IN")}</div>
    </div>
  </div>

  <!-- Dual score headline -->
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px;">
    <!-- PT overall -->
    <div style="background:#F0FDF4;border-left:5px solid #16A34A;border-radius:0 10px 10px 0;padding:14px 16px;">
      <div style="font-size:8px;font-weight:800;color:#16A34A;letter-spacing:.5px;margin-bottom:5px;">
        📊 PROGRESS TRACKER · NCF-FS 2022
      </div>
      <div style="display:flex;align-items:flex-end;gap:10px;">
        <div style="font-size:34px;font-weight:900;color:${ptLvColor(overall)};line-height:1;">${overall}%</div>
        <div>
          <div style="font-size:8px;color:#666;">40 indicators · 5 domains</div>
          <div style="font-size:8px;color:#666;">Cumulative · teacher-rated</div>
          <div style="background:${ptLvBg(overall)};color:${ptLvColor(overall)};font-weight:800;
            font-size:8px;padding:2px 8px;border-radius:6px;display:inline-block;margin-top:3px;">
            ${ptLvLabel(overall)}
          </div>
        </div>
      </div>
    </div>
    <!-- SRT overall -->
    <div style="background:${srtData ? "#E6F5F3" : "#F9FAFB"};border-left:5px solid ${srtData ? "#0E8A7A" : "#D1D5DB"};border-radius:0 10px 10px 0;padding:14px 16px;">
      <div style="font-size:8px;font-weight:800;color:${srtData ? "#0E8A7A" : "#9CA3AF"};letter-spacing:.5px;margin-bottom:5px;">
        🎮 SCHOOL READINESS TEST · PANCHAKOSHA
      </div>
      ${srtData ? `
      <div style="display:flex;align-items:flex-end;gap:10px;">
        <div style="font-size:34px;font-weight:900;color:${lvColor(srtOverall)};line-height:1;">${srtOverall}%</div>
        <div>
          <div style="font-size:8px;color:#666;">90 items · 5 koshas · ${srtItems} assessed</div>
          <div style="font-size:8px;color:#666;">Snapshot · assessed ${srtDate}</div>
          <div style="background:${lvBg(srtOverall)};color:${lvColor(srtOverall)};font-weight:800;
            font-size:8px;padding:2px 8px;border-radius:6px;display:inline-block;margin-top:3px;">
            ${lvLabel(srtOverall)}
          </div>
        </div>
      </div>` : `<div style="font-size:10px;color:#9CA3AF;margin-top:4px;">No SRT conducted yet</div>`}
    </div>
  </div>

  <!-- Methodology note -->
  <div style="background:#FFFBEA;border:1px solid #E8C84A;border-radius:8px;padding:9px 13px;margin-bottom:14px;font-size:7.5px;color:#7A6000;line-height:1.7;">
    <strong>Reading this report:</strong> Progress Tracker (PT) scores reflect cumulative teacher observations across multiple sessions —
    all 40 indicators are included in the denominator (not-observed = 0). School Readiness Test (SRT) scores reflect
    single-session performance across 90 play-based tasks — only interacted items count in the denominator.
    These instruments are <em>complementary</em>, not redundant. High PT + lower SRT may mean the child performs
    better in familiar ongoing contexts than in structured test conditions, which is developmentally normal.
  </div>

  <!-- Domain × Kosha side-by-side grid -->
  <div style="margin-bottom:4px;">
    <div style="font-size:9px;font-weight:800;color:#374151;letter-spacing:.4px;margin-bottom:8px;
      border-bottom:2px solid #E5E7EB;padding-bottom:4px;">
      COMPARATIVE OVERVIEW — NCF DOMAINS vs PANCHAKOSHA KOSHAS
    </div>
    <table style="font-size:8.5px;">
      <thead>
        <tr style="background:#F3F4F6;">
          <th style="padding:5px 8px;text-align:left;width:140px;">NCF Domain (PT)</th>
          <th style="padding:5px 8px;text-align:center;width:60px;">PT Score</th>
          <th style="padding:5px 8px;text-align:left;width:140px;">Panchakosha (SRT)</th>
          <th style="padding:5px 8px;text-align:center;width:60px;">SRT Score</th>
          <th style="padding:5px 8px;text-align:left;">Mapping note</th>
        </tr>
      </thead>
      <tbody>
        ${(() => {
          const MAPPING = [
            { ptId:"physical-motor", koshaId:"annamaya",    note:"Nearest match — motor tasks" },
            { ptId:"socio-emotional",koshaId:"pranamaya",   note:"Good match — emotional/social" },
            { ptId:"cognitive",      koshaId:"manomaya",    note:"Good match — reasoning/memory" },
            { ptId:"language-literacy",koshaId:"vijnanamaya",note:"Partial — SRT also covers numeracy" },
            { ptId:null,             koshaId:"anandamaya",  note:"No NCF equivalent — joy/creativity" },
          ];
          return MAPPING.map(m => {
            const pt    = ptScores.find(d => d.label === DOMAINS.find(x=>x.id===m.ptId)?.label);
            const kosha = KOSHA_LIST.find(k => k.id === m.koshaId);
            const ptPct = pt ? pt.pct : null;
            const srtPct = srtData ? Math.round(srtData.koshaScores?.[m.koshaId] ?? 0) : null;
            const gap   = (ptPct != null && srtPct != null) ? (srtPct - ptPct) : null;
            return `<tr style="border-bottom:1px solid #F3F4F6;">
              <td style="padding:5px 8px;font-weight:700;color:${pt?.color||"#9CA3AF"};">
                ${pt ? pt.icon + " " + pt.label : "—"}
              </td>
              <td style="padding:5px 8px;text-align:center;">
                ${ptPct != null
                  ? `<span style="font-weight:900;color:${ptLvColor(ptPct)};">${ptPct}%</span>`
                  : '<span style="color:#D1D5DB;">—</span>'}
              </td>
              <td style="padding:5px 8px;font-weight:700;color:${kosha.color};">
                ${kosha.icon} ${kosha.label}
              </td>
              <td style="padding:5px 8px;text-align:center;">
                ${srtPct != null
                  ? `<span style="font-weight:900;color:${lvColor(srtPct)};">${srtPct}%</span>`
                  : '<span style="color:#D1D5DB;">—</span>'}
              </td>
              <td style="padding:5px 8px;color:#6B7280;font-style:italic;">
                ${m.note}
                ${gap != null ? `<span style="font-weight:700;color:${gap>10?"#16A34A":gap<-10?"#DC2626":"#B45309"}">  Δ${gap>0?"+":""}${gap}%</span>` : ""}
              </td>
            </tr>`;
          }).join("");
        })()}
      </tbody>
    </table>
  </div>
</div>

<!-- ── PAGE 2: NCF Indicators detail ── -->
<div class="page-break">
  <div style="font-size:11px;font-weight:900;color:#374151;margin-bottom:12px;
    border-bottom:2px solid #EA580C;padding-bottom:6px;">
    📊 PROGRESS TRACKER — NCF-FS 2022 DOMAIN INDICATORS
    <span style="font-size:8px;font-weight:500;color:#888;margin-left:8px;">
      Cumulative teacher ratings · all sessions · Not-observed = 0 in denominator
    </span>
  </div>

  <!-- Domain score summary -->
  <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:14px;">
    ${ptScores.map(d => `
      <div style="background:${d.bg};border-left:3px solid ${d.color};border-radius:0 7px 7px 0;padding:7px 9px;text-align:center;">
        <div style="font-size:10px;">${d.icon}</div>
        <div style="font-size:16px;font-weight:900;color:${d.color};line-height:1;">${d.pct}%</div>
        <div style="font-size:7px;color:${d.color};margin-top:2px;">${d.label.split(" ")[0]}</div>
        <div style="height:3px;background:white;border-radius:2px;margin-top:4px;overflow:hidden;">
          <div style="width:${d.pct}%;height:100%;background:${d.color};"></div>
        </div>
      </div>`).join("")}
  </div>

  <!-- Indicator tables -->
  ${ptScores.map(d => `
    <div style="margin-bottom:12px;">
      <div style="font-size:8.5px;font-weight:800;color:${d.color};letter-spacing:.4px;margin-bottom:4px;
        display:flex;justify-content:space-between;align-items:center;">
        <span>${d.icon} ${d.label.toUpperCase()} · ${d.pct}%</span>
        <span style="font-size:7px;font-weight:500;color:#888;">8 indicators · max score 24</span>
      </div>
      <table style="font-size:8.5px;">
        <thead><tr style="background:#F9FAFB;">
          <th style="padding:3px 7px;text-align:left;width:52%;">Indicator</th>
          <th style="padding:3px 7px;width:90px;">Level</th>
          <th style="padding:3px 7px;">Observation note</th>
        </tr></thead>
        <tbody>
          ${d.indicators.map(ind => {
            const lvl = LEVELS.find(l => l.id === ind.level) || LEVELS[0];
            return `<tr style="border-bottom:1px solid #F5F5F5;">
              <td style="padding:3px 7px;color:#374151;">${ind.label}</td>
              <td style="padding:3px 7px;">
                <span style="background:${lvl.bg};color:${lvl.color};font-weight:800;
                  padding:1px 6px;border-radius:4px;font-size:7.5px;">${lvl.label}</span>
              </td>
              <td style="padding:3px 7px;color:#9CA3AF;font-style:italic;font-size:7.5px;">${ind.note}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
    </div>`).join("")}
</div>

<!-- ── PAGE 3: Panchakosha SRT detail ── -->
${srtData ? `<div class="page-break">
  <div style="font-size:11px;font-weight:900;color:#374151;margin-bottom:12px;
    border-bottom:2px solid #1A4F7A;padding-bottom:6px;">
    🎮 SCHOOL READINESS TEST — PANCHAKOSHA PROFILE
    <span style="font-size:8px;font-weight:500;color:#888;margin-left:8px;">
      Assessed ${srtDate} · ${srtItems}/90 items · Snapshot performance
    </span>
  </div>

  <!-- Kosha score summary -->
  <div style="display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:14px;">
    ${KOSHA_LIST.map(k => {
      const pct = Math.round(srtData.koshaScores?.[k.id] ?? 0);
      return `<div style="background:${lvBg(pct)};border-left:3px solid ${k.color};border-radius:0 7px 7px 0;padding:7px 9px;text-align:center;">
        <div style="font-size:10px;">${k.icon}</div>
        <div style="font-size:16px;font-weight:900;color:${k.color};line-height:1;">${pct}%</div>
        <div style="font-size:7px;color:${k.color};margin-top:2px;">${k.id.slice(0,6)}</div>
        <div style="height:3px;background:white;border-radius:2px;margin-top:4px;overflow:hidden;">
          <div style="width:${pct}%;height:100%;background:${k.color};"></div>
        </div>
      </div>`;
    }).join("")}
  </div>

  <div style="font-size:7.5px;color:#6B7280;background:#F8FAFC;border-radius:6px;padding:8px 11px;margin-bottom:10px;line-height:1.7;">
    <strong>SRT Scoring:</strong> Each kosha has 18 items. Items not attempted are excluded from the denominator.
    Score = sum(interacted item scores) ÷ (interacted count × 3) × 100.
    Only items marked "interacted" contribute — avoiding penalising children for items skipped due to disability or age.
  </div>

  <!-- Kosha detail table -->
  ${KOSHA_LIST.map(k => {
    const pct = Math.round(srtData.koshaScores?.[k.id] ?? 0);
    return `<div style="margin-bottom:12px;">
      <div style="font-size:8.5px;font-weight:800;color:${k.color};letter-spacing:.4px;margin-bottom:4px;
        display:flex;justify-content:space-between;align-items:center;">
        <span>${k.icon} ${k.label.toUpperCase()} · ${k.subtitle}</span>
        <span style="font-size:7px;font-weight:700;color:${lvColor(pct)};">${pct}% — ${lvLabel(pct)}</span>
      </div>
      <div style="font-size:7.5px;color:#888;margin-bottom:4px;">↔ Nearest NCF domain: ${k.ncfLabel}</div>
      <div style="height:5px;background:#F3F4F6;border-radius:3px;overflow:hidden;margin-bottom:6px;">
        <div style="width:${pct}%;height:100%;background:${k.color};border-radius:3px;"></div>
      </div>
    </div>`;
  }).join("")}
</div>` : ""}

<!-- Footer -->
<div style="border-top:1px solid #E5E7EB;padding-top:8px;margin-top:16px;
  display:flex;justify-content:space-between;font-size:7px;color:#9CA3AF;">
  <span>
    Progress Tracker: cumulative NCF-FS 2022 indicator ratings.
    SRT: single-session Panchakosha play-based assessment.
    Results should be reviewed with a qualified special educator. © NISH Trivandrum · NCAHT · ICMR · Vidyagni
  </span>
  <span>Assessor: _________________________________ Date: ${new Date().toLocaleDateString("en-IN")}</span>
</div>

</body></html>`;

  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 700);
}

// ─── KoshaRadarMini ──────────────────────────────────────────────────────────
const KOSHA_META = [
  { id:"annamaya",    icon:"🏃", color:"#0E8A7A" },
  { id:"pranamaya",   icon:"💛", color:"#C2620A" },
  { id:"manomaya",    icon:"🧠", color:"#6E3EA6" },
  { id:"vijnanamaya", icon:"📚", color:"#1A4F7A" },
  { id:"anandamaya",  icon:"🎨", color:"#B03060" },
];

function KoshaRadarMini({ scores, size = 90 }) {
  const cx=size/2, cy=size/2, r=size*.36, n=KOSHA_META.length;
  const ang = i => (Math.PI*2*i/n) - Math.PI/2;
  const pt  = (v, i) => [cx + r*(v/100)*Math.cos(ang(i)), cy + r*(v/100)*Math.sin(ang(i))];
  const pcts = KOSHA_META.map(k => scores[k.id] ?? 0);
  return (
    <svg width={size} height={size}>
      {[25,50,75,100].map(p => (
        <polygon key={p} points={KOSHA_META.map((_,i)=>pt(p,i).join(",")).join(" ")} fill="none" stroke="#DDE4EC" strokeWidth={.7}/>
      ))}
      {KOSHA_META.map((_,i) => {
        const p = pt(100,i);
        return <line key={i} x1={cx} y1={cy} x2={p[0]} y2={p[1]} stroke="#DDE4EC" strokeWidth={.7}/>;
      })}
      <polygon points={pcts.map((s,i)=>pt(s,i).join(",")).join(" ")}
        fill="rgba(180,48,96,.12)" stroke="#B03060" strokeWidth={1.5}/>
      {pcts.map((s,i) => {
        const p = pt(s,i);
        return <circle key={i} cx={p[0]} cy={p[1]} r={2.5} fill={KOSHA_META[i].color}/>;
      })}
      {KOSHA_META.map((k,i) => (
        <text key={i} x={cx+(r+16)*Math.cos(ang(i))} y={cy+(r+16)*Math.sin(ang(i))}
          textAnchor="middle" dominantBaseline="middle" fontSize={8} fill={k.color}>{k.icon}</text>
      ))}
    </svg>
  );
}

// ─── ReadinessPanel ──────────────────────────────────────────────────────────
function ReadinessPanel({ srtData, srtLoad, child, domainScores, overall, overallLvl }) {
  // ── KOSHA ↔ NCF DOMAIN mapping for side-by-side comparison ──────────────
  // SRT Panchakosha koshas map approximately (not 1:1) to PT NCF domains
  const KOSHA_NCF_MAP = [
    {
      koshaId: "annamaya",    koshaLabel: "Annamaya",    koshaIcon: "🏃",
      koshaColor: "#0E8A7A",  koshaSubtitle: "Physical Body",
      ncfId: "physical-motor", ncfLabel: "Physical & Motor", ncfIcon: "🏃",
      ncfColor: "#16A34A",
      note: "Nearest match. SRT measures 18 observable gross/fine motor tasks; PT rates 8 indicators over time.",
    },
    {
      koshaId: "pranamaya",   koshaLabel: "Pranamaya",   koshaIcon: "💛",
      koshaColor: "#C2620A",  koshaSubtitle: "Vital Energy & Emotions",
      ncfId: "socio-emotional", ncfLabel: "Socio-Emotional", ncfIcon: "💛",
      ncfColor: "#EA580C",
      note: "Good match. SRT uses live observation of emotional regulation; PT uses cumulative rubric ratings.",
    },
    {
      koshaId: "manomaya",    koshaLabel: "Manomaya",    koshaIcon: "🧠",
      koshaColor: "#6E3EA6",  koshaSubtitle: "Mind & Perception",
      ncfId: "cognitive",     ncfLabel: "Cognitive",     ncfIcon: "🧠",
      ncfColor: "#9333EA",
      note: "Good match. SRT uses pattern/reasoning tasks in session; PT tracks indicators across sessions.",
    },
    {
      koshaId: "vijnanamaya", koshaLabel: "Vijnanamaya", koshaIcon: "📚",
      koshaColor: "#1A4F7A",  koshaSubtitle: "Intellect & Language",
      ncfId: "language-literacy", ncfLabel: "Language & Literacy", ncfIcon: "📚",
      ncfColor: "#2563EB",
      note: "Partial match. SRT covers both language + numeracy; PT splits into Language (8 ind.) + Numeracy (8 ind.).",
    },
    {
      koshaId: "anandamaya",  koshaLabel: "Anandamaya",  koshaIcon: "🎨",
      koshaColor: "#B03060",  koshaSubtitle: "Joy, Play & Creativity",
      ncfId: null,            ncfLabel: "No direct match", ncfIcon: "—",
      ncfColor: "#9CA3AF",
      note: "No equivalent in PT. Anandamaya covers creative-aesthetic joy — unique to the Panchakosha framework.",
    },
  ];

  // PT numeracy domain score (separate from language in PT)
  const numeracyScore = domainScores?.find(d => d.id === "numeracy")?.pct ?? null;

  const srtOverall = srtData ? Math.round(srtData.overallScore ?? 0) : null;
  const srtDate    = srtData?.date
    ? new Date(srtData.date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })
    : null;
  const srtItems   = srtData ? (Object.values(srtData.responses || {}).filter(r => r.interacted).length) : 0;

  if (srtLoad) return (
    <div style={{ display:"flex", justifyContent:"center", padding:60 }}>
      <div style={{ width:28, height:28, border:`3px solid #E5E7EB`,
        borderTop:`3px solid ${T.agniCore}`, borderRadius:"50%", animation:"spin .7s linear infinite" }}/>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* ── Methodology explanation banner ── */}
      <div style={{ background:"#F0F9FF", borderRadius:14, padding:"16px 20px", border:"1.5px solid #BAE6FD" }}>
        <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:15, fontWeight:900, color:"#0C4A6E", marginBottom:10 }}>
          📊 Two Different Measurements — Both Valid
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <div style={{ background:"white", borderRadius:10, padding:"12px 14px", borderLeft:"4px solid #0E8A7A" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#0E8A7A", marginBottom:6 }}>🎮 School Readiness Test (SRT)</div>
            <div style={{ fontSize:11, color:"#374151", lineHeight:1.7 }}>
              <strong>What:</strong> 90 play-based tasks across 5 Panchakosha koshas<br/>
              <strong>How:</strong> Single session, in-the-moment performance<br/>
              <strong>Denominator:</strong> Only <em>assessed</em> items count (unrated excluded)<br/>
              <strong>Formula:</strong> sum(item scores) ÷ (assessed × 3) × 100 per kosha<br/>
              <strong>Overall:</strong> Average of 5 kosha percentages
            </div>
          </div>
          <div style={{ background:"white", borderRadius:10, padding:"12px 14px", borderLeft:"4px solid #16A34A" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#16A34A", marginBottom:6 }}>📊 Progress Tracker (PT)</div>
            <div style={{ fontSize:11, color:"#374151", lineHeight:1.7 }}>
              <strong>What:</strong> 40 NCF-FS 2022 indicators across 5 domains<br/>
              <strong>How:</strong> Cumulative teacher ratings across multiple sessions<br/>
              <strong>Denominator:</strong> ALL indicators included (not-observed = 0)<br/>
              <strong>Formula:</strong> sum(all indicator scores) ÷ (8 × 3) × 100 per domain<br/>
              <strong>Overall:</strong> Average of 5 domain percentages
            </div>
          </div>
        </div>
        <div style={{ marginTop:10, fontSize:11, color:"#0C4A6E", background:"#E0F2FE", borderRadius:8, padding:"8px 12px", fontWeight:500, lineHeight:1.6 }}>
          💡 Why they differ: SRT is a <strong>snapshot</strong> of in-session performance across 90 items; 
          PT is a <strong>running record</strong> of teacher-observed development across 40 indicators. 
          A child may perform differently under assessment conditions vs. natural classroom observation. 
          Both together give a fuller picture than either alone.
        </div>
      </div>

      {/* ── Headline scores side by side ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        {/* PT overall */}
        <div style={{ background:"white", borderRadius:14, padding:"20px 22px", border:`1.5px solid #E5E7EB`,
          boxShadow:"0 2px 10px rgba(0,0,0,.06)", textAlign:"center" }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.muted, letterSpacing:.8, marginBottom:8 }}>
            PROGRESS TRACKER OVERALL
          </div>
          <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:48, fontWeight:900,
            color:overallLvl?.color||T.muted, lineHeight:1 }}>
            {overall ?? "–"}%
          </div>
          <div style={{ fontSize:12, color:T.muted, marginBottom:8 }}>NCF-FS 2022 · 5 domains · 40 indicators</div>
          <div style={{ background:overallLvl?.bg||"#F9FAFB", color:overallLvl?.color||T.muted,
            fontWeight:700, fontSize:12, padding:"4px 14px", borderRadius:20, display:"inline-block",
            border:`1px solid ${overallLvl?.color||"#E5E7EB"}40` }}>
            {overallLvl?.label||"Not rated"}
          </div>
          <div style={{ marginTop:12, fontSize:10, color:T.muted, fontStyle:"italic" }}>
            Cumulative · based on all sessions
          </div>
        </div>

        {/* SRT overall */}
        <div style={{ background:"white", borderRadius:14, padding:"20px 22px", border:`1.5px solid #E5E7EB`,
          boxShadow:"0 2px 10px rgba(0,0,0,.06)", textAlign:"center", opacity: srtData ? 1 : 0.55 }}>
          <div style={{ fontSize:11, fontWeight:800, color:T.muted, letterSpacing:.8, marginBottom:8 }}>
            SCHOOL READINESS TEST
          </div>
          {srtData ? (
            <>
              <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:48, fontWeight:900,
                color:levelFor(srtOverall).color, lineHeight:1 }}>
                {srtOverall}%
              </div>
              <div style={{ fontSize:12, color:T.muted, marginBottom:8 }}>
                Panchakosha · 90 items · {srtItems} assessed
              </div>
              <div style={{ background:levelFor(srtOverall).bg, color:levelFor(srtOverall).color,
                fontWeight:700, fontSize:12, padding:"4px 14px", borderRadius:20, display:"inline-block" }}>
                {levelFor(srtOverall).label}
              </div>
              <div style={{ marginTop:12, fontSize:10, color:T.muted, fontStyle:"italic" }}>
                Snapshot · assessed {srtDate}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize:36, marginBottom:8 }}>🔍</div>
              <div style={{ fontSize:13, color:T.muted }}>No SRT data yet</div>
              <div style={{ fontSize:11, color:T.muted, marginTop:6, fontStyle:"italic" }}>
                Run a School Readiness Test to see comparison
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Domain vs Kosha comparison table ── */}
      <div style={{ background:"white", borderRadius:14, border:"1.5px solid #E5E7EB",
        boxShadow:"0 1px 6px rgba(0,0,0,.05)", overflow:"hidden" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #F3F4F6", background:"#FAFBFC" }}>
          <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:14, fontWeight:900, color:T.slate }}>
            Domain × Kosha Score Comparison
          </div>
          <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>
            Approximate mapping — frameworks are not 1:1 equivalent
          </div>
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#F9FAFB" }}>
              <th style={{ padding:"10px 16px", textAlign:"left", fontSize:10, color:T.muted, fontWeight:700, width:180 }}>Framework</th>
              <th style={{ padding:"10px 12px", textAlign:"center", fontSize:10, color:"#16A34A", fontWeight:800 }}>📊 Progress Tracker</th>
              <th style={{ padding:"10px 12px", textAlign:"center", fontSize:10, color:"#0E8A7A", fontWeight:800 }}>🎮 School Readiness</th>
              <th style={{ padding:"10px 12px", textAlign:"center", fontSize:10, color:T.muted, fontWeight:700 }}>Gap</th>
              <th style={{ padding:"10px 16px", textAlign:"left", fontSize:10, color:T.muted, fontWeight:700 }}>Note</th>
            </tr>
          </thead>
          <tbody>
            {KOSHA_NCF_MAP.map((row, i) => {
              const ptScore  = row.ncfId ? (domainScores?.find(d => d.id === row.ncfId)?.pct ?? null) : null;
              // For vijnanamaya: average PT language + numeracy
              const ptAdjusted = row.koshaId === "vijnanamaya" && ptScore !== null && numeracyScore !== null
                ? Math.round((ptScore + numeracyScore) / 2)
                : ptScore;
              const srtScore = srtData ? Math.round(srtData.koshaScores?.[row.koshaId] ?? 0) : null;
              const gap      = (ptAdjusted != null && srtScore != null) ? (srtScore - ptAdjusted) : null;
              const gapColor = gap == null ? T.muted : gap > 10 ? "#16A34A" : gap < -10 ? T.red : T.amber;

              return (
                <tr key={row.koshaId} style={{ borderBottom:"1px solid #F3F4F6" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#FAFBFC"}
                  onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <td style={{ padding:"12px 16px" }}>
                    <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <span style={{ fontSize:14 }}>{row.koshaIcon}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:row.koshaColor }}>{row.koshaLabel}</span>
                      </div>
                      <div style={{ fontSize:10, color:T.muted, paddingLeft:20 }}>{row.koshaSubtitle}</div>
                    </div>
                  </td>
                  {/* PT score */}
                  <td style={{ padding:"12px 12px", textAlign:"center" }}>
                    {ptAdjusted != null ? (
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                        <div style={{ fontSize:18, fontWeight:900, color:levelFor(ptAdjusted).color }}>{ptAdjusted}%</div>
                        <div style={{ width:60, height:5, background:"#F3F4F6", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ width:`${ptAdjusted}%`, height:"100%", background:"#16A34A", borderRadius:3 }}/>
                        </div>
                        {row.koshaId === "vijnanamaya" && (
                          <div style={{ fontSize:9, color:T.muted, fontStyle:"italic" }}>avg Lang+Num</div>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize:11, color:"#D1D5DB" }}>—</span>
                    )}
                  </td>
                  {/* SRT score */}
                  <td style={{ padding:"12px 12px", textAlign:"center" }}>
                    {srtScore != null ? (
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                        <div style={{ fontSize:18, fontWeight:900, color:levelFor(srtScore).color }}>{srtScore}%</div>
                        <div style={{ width:60, height:5, background:"#F3F4F6", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ width:`${srtScore}%`, height:"100%", background:row.koshaColor, borderRadius:3 }}/>
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize:11, color:"#D1D5DB" }}>—</span>
                    )}
                  </td>
                  {/* Gap */}
                  <td style={{ padding:"12px 12px", textAlign:"center" }}>
                    {gap != null ? (
                      <div style={{ fontSize:13, fontWeight:800, color:gapColor }}>
                        {gap > 0 ? "+" : ""}{gap}%
                      </div>
                    ) : <span style={{ fontSize:11, color:"#D1D5DB" }}>—</span>}
                  </td>
                  {/* Note */}
                  <td style={{ padding:"12px 16px", maxWidth:220 }}>
                    <div style={{ fontSize:10, color:T.muted, lineHeight:1.5 }}>{row.note}</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Gap legend */}
        <div style={{ padding:"10px 16px", borderTop:"1px solid #F3F4F6", background:"#FAFBFC",
          display:"flex", gap:14, flexWrap:"wrap" }}>
          <span style={{ fontSize:10, color:T.muted }}>Gap = SRT − PT:</span>
          {[[T.red, "< −10% (PT higher)"],["#B45309", "−10 to +10% (similar)"],["#16A34A", "> +10% (SRT higher)"]].map(([c,l]) => (
            <div key={l} style={{ display:"flex", alignItems:"center", gap:5 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:c }}/>
              <span style={{ fontSize:10, color:T.muted }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── SRT Kosha bars (when data exists) ── */}
      {srtData && (
        <div style={{ background:"white", borderRadius:14, padding:"16px 20px",
          border:"1.5px solid #E5E7EB", boxShadow:"0 1px 6px rgba(0,0,0,.05)" }}>
          <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:14, fontWeight:900,
            color:T.slate, marginBottom:14 }}>Panchakosha Profile — {srtDate}</div>
          {[
            { id:"annamaya",    label:"Annamaya",    icon:"🏃", color:"#0E8A7A" },
            { id:"pranamaya",   label:"Pranamaya",   icon:"💛", color:"#C2620A" },
            { id:"manomaya",    label:"Manomaya",    icon:"🧠", color:"#6E3EA6" },
            { id:"vijnanamaya", label:"Vijnanamaya", icon:"📚", color:"#1A4F7A" },
            { id:"anandamaya",  label:"Anandamaya",  icon:"🎨", color:"#B03060" },
          ].map(k => {
            const pct = Math.round(srtData.koshaScores?.[k.id] ?? 0);
            const lv  = levelFor(pct);
            return (
              <div key={k.id} style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:k.color }}>{k.icon} {k.label}</span>
                  <span style={{ fontSize:12, fontWeight:800, color:lv.color }}>{pct}%</span>
                </div>
                <div style={{ height:8, background:"#F3F4F6", borderRadius:5, overflow:"hidden" }}>
                  <div style={{ width:`${pct}%`, height:"100%", background:k.color, borderRadius:5, transition:"width .5s" }}/>
                </div>
              </div>
            );
          })}
          <div style={{ marginTop:10, padding:"9px 12px", background:"#EFF6FF",
            borderRadius:8, border:"1px solid #93C5FD", fontSize:11, color:T.blue }}>
            View the full 90-item assessment and session history in the{" "}
            <strong>School Readiness</strong> module from the sidebar.
          </div>
        </div>
      )}

      {/* ── No SRT data prompt ── */}
      {!srtData && (
        <div style={{ background:"white", borderRadius:14, padding:"24px 20px",
          border:"1.5px solid #E5E7EB", textAlign:"center" }}>
          <div style={{ fontSize:36, marginBottom:10 }}>🎮</div>
          <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:15, fontWeight:800,
            color:T.slate, marginBottom:6 }}>No School Readiness Test yet</div>
          <div style={{ fontSize:13, color:T.muted, lineHeight:1.8 }}>
            Run a <strong>School Readiness Test</strong> for {child?.name} to unlock<br/>
            the side-by-side comparison with Progress Tracker scores.
          </div>
        </div>
      )}

    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function ProgressTracker() {
  const { getAuthHeader } = useAuth();

  const [children,        setChildren]        = useState([]);
  const [selectedChild,   setSelectedChild]   = useState(null);
  const [activeTab,       setActiveTab]       = useState("suggestions");
  const [devRecord,       setDevRecord]       = useState({});
  const [dirty,           setDirty]           = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [saveMsg,         setSaveMsg]         = useState("");
  const [analytics,       setAnalytics]       = useState(null);
  const [analyticsLoad,   setAnalyticsLoad]   = useState(false);
  const [srtData,         setSrtData]         = useState(null);   // latest SRTSession for selected child
  const [srtLoad,         setSrtLoad]         = useState(false);
  const [suggestions,     setSuggestions]     = useState([]);
  const [suggestLoad,     setSuggestLoad]     = useState(false);
  const [showAddChild,    setShowAddChild]    = useState(false);
  const [childForm,       setChildForm]       = useState({ name: "", dob: "", disabilities: [], color: "#EA580C" });
  const [pageLoading,     setPageLoading]     = useState(true);
  const [expandedDomains, setExpandedDomains] = useState(new Set(["language-literacy"]));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Load children
  useEffect(() => {
    ;(async () => {
      try {
        const res  = await fetch(`${API}/children?limit=100`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success && data.children.length) {
          setChildren(data.children);
          setSelectedChild(data.children[0]);
        }
      } catch {}
      setPageLoading(false);
    })();
  }, []);

  // Load dev record when child changes
  useEffect(() => {
    if (!selectedChild) return;
    setDevRecord({}); setDirty(false); setSuggestions([]);
    ;(async () => {
      try {
        const res  = await fetch(`${API}/progress/${selectedChild._id}`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success && data.record) {
          const flat = {};
          (data.record.indicators || []).forEach(i => {
            flat[`${i.domainId}::${i.indicatorId}`] = {
              level:   i.current?.level || "not-observed",
              note:    i.current?.note  || "",
              history: i.history || [],
            };
          });
          setDevRecord(flat);
        }
      } catch {}
    })();
  }, [selectedChild]);

  // Load analytics for suggestions + mastery panel
  useEffect(() => {
    if (!selectedChild) return;
    setAnalytics(null); setAnalyticsLoad(true);
    ;(async () => {
      try {
        const res  = await fetch(`${API}/flashcard-sessions/${selectedChild._id}/analytics?days=60`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setAnalytics(data.analytics);
      } catch {}
      setAnalyticsLoad(false);
    })();
  }, [selectedChild]);

  // Load latest SRT session for suggestions + readiness tab
  useEffect(() => {
    if (!selectedChild) return;
    setSrtData(null); setSrtLoad(true);
    ;(async () => {
      try {
        const res  = await fetch(`${API}/srt/${selectedChild._id}/latest`, { headers: getAuthHeader() });
        const data = await res.json();
        if (data.success) setSrtData(data.session || null);
      } catch {}
      setSrtLoad(false);
    })();
  }, [selectedChild]);

  // Rebuild suggestions when devRecord, analytics, or srtData change
  useEffect(() => {
    if (!selectedChild) return;
    setSuggestLoad(true);
    const t = setTimeout(() => {
      setSuggestions(buildSuggestions(devRecord, analytics, srtData));
      setSuggestLoad(false);
    }, 200);
    return () => clearTimeout(t);
  }, [devRecord, analytics, srtData]);

  function handleChange(key, value) {
    setDevRecord(r => ({ ...r, [key]: value }));
    setDirty(true);
  }

  async function saveRecord() {
    if (!selectedChild || !dirty) return;
    setSaving(true);
    try {
      const indicators = [];
      DOMAINS.forEach(d => {
        d.indicators.forEach(ind => {
          const key = `${d.id}::${ind.id}`;
          const rec = devRecord[key] || { level: "not-observed", note: "" };
          indicators.push({ indicatorId: ind.id, domainId: d.id, level: rec.level, note: rec.note || "" });
        });
      });
      const res  = await fetch(`${API}/progress/${selectedChild._id}`, {
        method: "PUT", headers: getAuthHeader(), body: JSON.stringify({ indicators }),
      });
      const data = await res.json();
      if (data.success) {
        setDirty(false);
        setSaveMsg("Saved ✓");
        setTimeout(() => setSaveMsg(""), 3000);
      }
    } catch {}
    setSaving(false);
  }

  async function addChild() {
    if (!childForm.name.trim()) return;
    try {
      const res  = await fetch(`${API}/children`, {
        method: "POST", headers: getAuthHeader(), body: JSON.stringify(childForm),
      });
      const data = await res.json();
      if (data.success) {
        setChildren(c => [...c, data.child]);
        setSelectedChild(data.child);
        setShowAddChild(false);
        setChildForm({ name: "", dob: "", disabilities: [], color: "#EA580C" });
      }
    } catch {}
  }

  const domainScores = DOMAINS.map(d => ({
    ...d, pct: domainScore(devRecord, d.id),
  }));
  const overall = Math.round(domainScores.reduce((s, d) => s + d.pct, 0) / domainScores.length);
  const overallLvl = levelFor(overall);
  const highPriority = suggestions.filter(s => s.priority === "high").length;

  if (pageLoading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, flexDirection: "column" }}>
      <div style={{ width: 28, height: 28, border: `3px solid #E5E7EB`, borderTop: `3px solid ${T.agniCore}`, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
      <div style={{ fontSize: 13, color: T.muted }}>Loading children…</div>
    </div>
  );

  return (
    <div style={{ fontFamily: "'Inter',system-ui,sans-serif", background: T.pageBg, minHeight: "100vh", color: T.slate }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg) } }
        * { box-sizing: border-box; }
      `}</style>

      {/* ══ TOP BAR ══ */}
      <div style={{ background: "#fff", borderBottom: `1px solid #E5E7EB`, padding: "13px 24px",
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 18, fontWeight: 900, color: T.slate }}>Progress Tracker</div>
          <div style={{ fontSize: 11, color: T.muted }}>NCF-FS 2022 · SM-2 Spaced Repetition · BKT Mastery · Elo</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {selectedChild && (
            <>
            <button onClick={() => printReport(selectedChild, devRecord, domainScores, overall)}
              style={{ padding: "8px 14px", background: "#fff", color: T.slate, border: `1.5px solid #E5E7EB`,
                borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              🖨️ NCF Report
            </button>
            <button onClick={() => printCombinedReport(selectedChild, devRecord, domainScores, overall, srtData)}
              style={{ padding: "8px 14px", background: T.nightInk, color: "#fff", border: "none",
                borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              🖨️ Combined Report
            </button>
            </>
          )}
          <button onClick={() => setShowAddChild(true)}
            style={{ padding: "8px 16px", background: T.pageBg, color: T.muted, border: `1.5px solid #E5E7EB`,
              borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            + Add Child
          </button>
        </div>
      </div>

      <div style={{ display: "flex", height: "calc(100vh - 64px)" }}>

        {/* ══ CHILDREN SIDEBAR ══ */}
        <div style={{ width: sidebarCollapsed ? 60 : 210, flexShrink: 0, background: "#fff", borderRight: `1px solid #E5E7EB`,
          overflowY: "auto", padding: "12px 0", transition: "width .18s ease" }}>

          {/* Collapse toggle */}
          <div style={{ display: "flex", justifyContent: sidebarCollapsed ? "center" : "flex-end",
            padding: sidebarCollapsed ? "0 0 10px" : "0 10px 10px" }}>
            <button onClick={() => setSidebarCollapsed(c => !c)}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{ background: T.pageBg, border: `1px solid #E5E7EB`, borderRadius: 7, width: 24, height: 24,
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                color: T.muted, fontSize: 11, flexShrink: 0 }}>
              {sidebarCollapsed ? "›" : "‹"}
            </button>
          </div>

          {children.length === 0 ? (
            sidebarCollapsed ? null : (
              <div style={{ textAlign: "center", padding: "32px 16px", color: T.muted, fontSize: 13 }}>
                No children yet.<br />
                <button onClick={() => setShowAddChild(true)}
                  style={{ marginTop: 10, color: T.agniCore, background: "none", border: "none",
                    cursor: "pointer", fontWeight: 700, fontSize: 13, textDecoration: "underline" }}>
                  Add first child
                </button>
              </div>
            )
          ) : children.map(child => {
            const sel = selectedChild?._id === child._id;
            return (
              <button key={child._id} onClick={() => setSelectedChild(child)}
                title={child.name}
                style={{ display: "block", width: "100%", textAlign: "left",
                  padding: sidebarCollapsed ? "7px 0" : "11px 14px",
                  background: sel ? "#FFF7ED" : "#fff",
                  borderLeft: sidebarCollapsed ? "none" : `3px solid ${sel ? child.color || T.agniCore : "transparent"}`,
                  border: "none", cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9,
                  justifyContent: sidebarCollapsed ? "center" : "flex-start" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%",
                    background: child.color || T.agniCore,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#fff", fontWeight: 900, fontSize: 14, flexShrink: 0,
                    fontFamily: "'Nunito',sans-serif",
                    boxShadow: sidebarCollapsed && sel ? `0 0 0 2px ${child.color || T.agniCore}` : "none" }}>
                    {child.name[0]}
                  </div>
                  {!sidebarCollapsed && (
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: sel ? T.agniCore : T.slate,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {child.name}
                      </div>
                      <div style={{ fontSize: 10, color: T.muted, lineHeight: 1.4 }}>
                        {(child.disabilities || []).slice(0, 1).join("") || "No disability recorded"}<br />
                        {calcAge(child.dob) || "Age unknown"}
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ══ MAIN CONTENT ══ */}
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          {!selectedChild ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: T.muted, fontSize: 14 }}>
              Select a child to view their progress
            </div>
          ) : (
            <>
              {/* Child header + domain overview */}
              <div style={{ background: "#fff", borderBottom: `1px solid #E5E7EB`, padding: "12px 22px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "nowrap" }}>
                  {/* Avatar + info */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flex: "0 0 auto" }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%",
                      background: selectedChild.color || T.agniCore,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontWeight: 900, fontSize: 20, fontFamily: "'Nunito',sans-serif" }}>
                      {selectedChild.name[0]}
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 900, fontSize: 18, color: T.slate }}>
                        {selectedChild.name}
                      </div>
                      <div style={{ fontSize: 12, color: T.muted }}>
                        {selectedChild.disabilities?.join(", ") || "No disability recorded"} · {calcAge(selectedChild.dob) || "Age unknown"}
                      </div>
                      {selectedChild.iepGoals && (
                        <div style={{ fontSize: 11, color: T.muted, marginTop: 2, fontStyle: "italic" }}>
                          IEP: {selectedChild.iepGoals.slice(0, 60)}{selectedChild.iepGoals.length > 60 ? "…" : ""}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Radar chart — domain */}
                  <div style={{ flex: "0 0 auto", mb:'1' }}>
                    <div style={{ fontSize:9, fontWeight:700, color:T.muted, marginBottom:3, letterSpacing:.5, textAlign:"center" }}>NCF DOMAINS</div>
                    <RadarChart scores={domainScores} size={90} />
                  </div>

                  {/* Kosha radar — only if SRT data available */}
                  {srtData?.koshaScores && (
                    <div style={{ flex:"0 0 auto" }}>
                      <div style={{ fontSize:9, fontWeight:700, color:T.muted, marginBottom:3, letterSpacing:.5, textAlign:"center" }}>PANCHAKOSHA</div>
                      <KoshaRadarMini scores={srtData.koshaScores} size={90}/>
                    </div>
                  )}

                  {/* Dual score block: PT + SRT side by side */}
                  <div style={{ display:"flex", gap:10, flex:"0 0 auto", paddingTop:4, alignItems:"stretch" }}>
                    {/* PT score */}
                    <div style={{ textAlign:"center", minWidth:80,
                      background:"#F9FAFB", borderRadius:12, padding:"10px 14px",
                      border:"1.5px solid #E5E7EB" }}>
                      <div style={{ fontSize:9, fontWeight:800, color:T.muted, letterSpacing:.7, marginBottom:4 }}>PROGRESS</div>
                      <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:30, fontWeight:900,
                        color:overallLvl.color, lineHeight:1 }}>{overall}%</div>
                      <div style={{ fontSize:9, color:T.muted, marginTop:2 }}>NCF-FS</div>
                      <div style={{ marginTop:5, fontSize:10, fontWeight:700,
                        color:overallLvl.color, background:overallLvl.bg,
                        border:`1px solid ${overallLvl.color}40`,
                        padding:"2px 8px", borderRadius:20 }}>
                        {overallLvl.label}
                      </div>
                    </div>
                    {/* SRT score */}
                    {srtData ? (() => {
                      const srtPct = Math.round(srtData.overallScore ?? 0);
                      const srtLv  = levelFor(srtPct);
                      return (
                        <div style={{ textAlign:"center", minWidth:80,
                          background:"#E6F5F3", borderRadius:12, padding:"10px 14px",
                          border:"1.5px solid #0E8A7A40" }}>
                          <div style={{ fontSize:9, fontWeight:800, color:"#0E8A7A", letterSpacing:.7, marginBottom:4 }}>READINESS</div>
                          <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:30, fontWeight:900,
                            color:srtLv.color, lineHeight:1 }}>{srtPct}%</div>
                          <div style={{ fontSize:9, color:"#0E8A7A", marginTop:2 }}>Panchakosha</div>
                          <div style={{ marginTop:5, fontSize:10, fontWeight:700,
                            color:srtLv.color, background:srtLv.bg,
                            border:`1px solid ${srtLv.color}40`,
                            padding:"2px 8px", borderRadius:20 }}>
                            {srtLv.label}
                          </div>
                        </div>
                      );
                    })() : (
                      <div style={{ textAlign:"center", minWidth:80,
                        background:"#F9FAFB", borderRadius:12, padding:"10px 14px",
                        border:"1.5px solid #E5E7EB", opacity:0.5 }}>
                        <div style={{ fontSize:9, fontWeight:800, color:T.muted, letterSpacing:.7, marginBottom:4 }}>READINESS</div>
                        <div style={{ fontSize:24, marginTop:4 }}>🎮</div>
                        <div style={{ fontSize:9, color:T.muted, marginTop:4 }}>No SRT yet</div>
                      </div>
                    )}
                  </div>

                </div>

                {/* Compact domain strip — single row beneath */}
                <div style={{ display:"flex", gap:6, marginTop:10, alignItems:"center", flexWrap:"wrap" }}>
                  <span style={{ fontSize:9, fontWeight:700, color:T.muted, letterSpacing:.6, flexShrink:0 }}>NCF DOMAINS</span>
                  {domainScores.map(d => (
                    <div key={d.id} style={{ display:"flex", alignItems:"center", gap:5,
                      background:d.bg, border:`1px solid ${d.border}`,
                      borderRadius:20, padding:"3px 10px 3px 7px", flexShrink:0 }}>
                      <span style={{ fontSize:11 }}>{d.icon}</span>
                      <span style={{ fontSize:11, fontWeight:800, color:d.color }}>{d.pct}%</span>
                      <span style={{ fontSize:9, color:d.color, opacity:.7 }}>{d.shortLabel}</span>
                    </div>
                  ))}
                  {srtData?.koshaScores && (
                    <>
                      <span style={{ fontSize:9, fontWeight:700, color:T.muted, letterSpacing:.6, flexShrink:0, marginLeft:6 }}>PANCHAKOSHA</span>
                      {[
                        { id:"annamaya",    icon:"🏃", color:"#0E8A7A", label:"Annamaya" },
                        { id:"pranamaya",   icon:"💛", color:"#C2620A", label:"Pranamaya" },
                        { id:"manomaya",    icon:"🧠", color:"#6E3EA6", label:"Manomaya" },
                        { id:"vijnanamaya", icon:"📚", color:"#1A4F7A", label:"Vijnanamaya" },
                        { id:"anandamaya",  icon:"🎨", color:"#B03060", label:"Anandamaya" },
                      ].map(k => {
                        const pct = Math.round(srtData.koshaScores[k.id] ?? 0);
                        return (
                          <div key={k.id} style={{ display:"flex", alignItems:"center", gap:5,
                            background:"#F8FAFD", border:`1px solid ${k.color}30`,
                            borderRadius:20, padding:"3px 10px 3px 7px", flexShrink:0 }}>
                            <span style={{ fontSize:11 }}>{k.icon}</span>
                            <span style={{ fontSize:11, fontWeight:800, color:k.color }}>{pct}%</span>
                            <span style={{ fontSize:9, color:k.color, opacity:.7 }}>{k.label.slice(0,5)}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div style={{ background: "#fff", borderBottom: `1px solid #E5E7EB`, padding: "0 22px", display: "flex", gap: 0 }}>
                {[
                  { id: "suggestions",  label: "🎯 Suggestions",       badge: highPriority > 0 ? highPriority : null },
                  { id: "development",  label: "🧩 Domain Indicators" },
                  { id: "mastery",      label: "🃏 SM-2 / BKT Mastery" },
                  { id: "readiness",    label: "🔍 School Readiness" },
                ].map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    style={{ padding: "12px 16px", background: "none", border: "none",
                      borderBottom: activeTab === tab.id ? `2.5px solid ${T.agniCore}` : "2.5px solid transparent",
                      color: activeTab === tab.id ? T.agniCore : T.muted,
                      fontWeight: activeTab === tab.id ? 800 : 600,
                      fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
                      display: "flex", alignItems: "center", gap: 6 }}>
                    {tab.label}
                    {tab.badge && (
                      <span style={{ background: T.red, color: "#fff", fontSize: 10, fontWeight: 800,
                        width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div style={{ flex: 1, padding: "20px 22px", overflowY: "auto" }}>

                {/* ── SUGGESTIONS ── */}
                {activeTab === "suggestions" && (
                  <SuggestionsPanel suggestions={suggestions} loading={suggestLoad || analyticsLoad} />
                )}

                {/* ── DEVELOPMENT INDICATORS ── */}
                {activeTab === "development" && (
                  <div>
                    {/* Save bar */}
                    {dirty && (
                      <div style={{ background: "#FEF9C3", border: `1.5px solid #FDE68A`, borderRadius: 11,
                        padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, color: "#92400E", fontWeight: 600 }}>⚠️ You have unsaved changes</span>
                        <button onClick={saveRecord} disabled={saving}
                          style={{ padding: "7px 20px", background: saving ? "#9CA3AF" : T.agniCore, color: "#fff",
                            border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: saving ? "default" : "pointer" }}>
                          {saving ? "Saving…" : "Save Progress"}
                        </button>
                      </div>
                    )}
                    {saveMsg && (
                      <div style={{ background: "#F0FDF4", border: `1.5px solid #86EFAC`, borderRadius: 11,
                        padding: "10px 16px", marginBottom: 16, color: T.green, fontWeight: 700, fontSize: 13 }}>
                        {saveMsg}
                      </div>
                    )}

                    {DOMAINS.map(domain => {
                      const score    = domainScore(devRecord, domain.id);
                      const expanded = expandedDomains.has(domain.id);
                      const domLvl   = levelFor(score);
                      const stuckCount = domain.indicators.filter(ind => {
                        const r = devRecord[`${domain.id}::${ind.id}`];
                        return r?.level === "emerging";
                      }).length;

                      return (
                        <div key={domain.id} style={{ background: "#fff", borderRadius: 14, marginBottom: 12,
                          border: `1.5px solid ${expanded ? domain.border : "#E5E7EB"}`,
                          overflow: "hidden", transition: "border-color .2s" }}>
                          <button
                            onClick={() => setExpandedDomains(s => {
                              const n = new Set(s);
                              n.has(domain.id) ? n.delete(domain.id) : n.add(domain.id);
                              return n;
                            })}
                            style={{ display: "flex", alignItems: "center", gap: 13, width: "100%",
                              padding: "14px 18px", background: expanded ? domain.bg : "#fff",
                              border: "none", cursor: "pointer", textAlign: "left" }}>
                            <span style={{ fontSize: 22 }}>{domain.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                                <span style={{ fontFamily: "'Nunito',sans-serif", fontSize: 15, fontWeight: 800, color: domain.color }}>{domain.label}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: domLvl.color,
                                  background: domLvl.bg, border: `1px solid ${domLvl.color}40`,
                                  padding: "1px 8px", borderRadius: 20 }}>{domLvl.label}</span>
                                {stuckCount > 0 && (
                                  <span style={{ fontSize: 10, fontWeight: 700, color: T.agniCore,
                                    background: "#FFF7ED", border: `1px solid #FED7AA`,
                                    padding: "1px 7px", borderRadius: 20 }}>
                                    {stuckCount} emerging
                                  </span>
                                )}
                              </div>
                              <Bar pct={score} color={domain.color} height={5} />
                            </div>
                            <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 22, fontWeight: 900, color: domain.color, minWidth: 52, textAlign: "right" }}>
                              {score}%
                            </div>
                            <span style={{ color: domain.color, fontSize: 12 }}>{expanded ? "▲" : "▼"}</span>
                          </button>

                          {expanded && (
                            <div style={{ padding: "12px 18px 16px" }}>
                              {domain.indicators.map(ind => (
                                <IndicatorRow key={ind.id} ind={ind} domainId={domain.id}
                                  devRecord={devRecord} onChange={handleChange} domain={domain} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Level legend */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8, paddingTop: 12, borderTop: `1px solid #F3F4F6` }}>
                      {LEVELS.map(l => (
                        <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 24, height: 24, borderRadius: 6, background: l.bg,
                            border: `1.5px solid ${l.color}`, display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 9, fontWeight: 800, color: l.color }}>{l.short}</div>
                          <span style={{ fontSize: 11, color: T.muted }}>{l.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── MASTERY ── */}
                {activeTab === "mastery" && (
                  <MasteryPanel childId={selectedChild._id} getAuthHeader={getAuthHeader} />
                )}

                {/* ── SCHOOL READINESS ── */}
                {activeTab === "readiness" && (
                  <ReadinessPanel
                    srtData={srtData} srtLoad={srtLoad} child={selectedChild}
                    domainScores={domainScores} overall={overall} overallLvl={overallLvl}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showAddChild && (
        <AddChildModal childForm={childForm} setChildForm={setChildForm}
          onAdd={addChild} onClose={() => setShowAddChild(false)} />
      )}
    </div>
  );
}